import { ChangeEvent, FocusEvent } from "react"

import { css, cx } from "@emotion/css"

import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import { ErrorsMap } from "api/http/utils"
import GhostTextInput from "ui/inputs/GhostTextInput"
import { MediaField } from "../../GlobalMediaTypes/types"
import MoreActions from "components/MoreActions"
import { Select } from "components/inputs/Select"
import { Switch } from "@material-ui/core"
import { WithAuthority } from "components/WithAuthority"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

type Props = {
  data?: Partial<MediaField>[]
  handleDelete?: (e?: any) => void
  onUpdate?: (
    name: string | undefined,
    cellName: string,
    value: unknown,
    mediaFieldId?: string,
  ) => Promise<void>
  onChange?: (
    evt: ChangeEvent<
      | HTMLInputElement
      | HTMLTextAreaElement
      | {
          name?: string | undefined
          value: unknown
        }
    >,
  ) => void
  errors?: ErrorsMap
}

const types = [
  { label: "String", value: "String" },
  { label: "Numeric", value: "Numeric" },
  { label: "Date", value: "Date" },
  { label: "Time", value: "Time" },
  { label: "Calculated", value: "Calculated" },
  { label: "List", value: "List" },
  { label: "Spots Plan", value: "SpotsPlan" },
]

export default function SimpleTable(props: Props) {
  const { t } = useI18n()
  const { data = [], handleDelete, onUpdate, onChange, errors } = props
  const { hasAuthority } = useAuthenticated()

  const handleBlur = (
    evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement> | undefined,
    name: string,
    mediaFieldId?: string | undefined,
  ) => {
    const { name: path = "", value = "" } = evt?.target ?? {}
    !!onUpdate && onUpdate(name, path, value, mediaFieldId)
  }
  const hasKey = (prop: string) => data.some((d: any) => d.hasOwnProperty(prop))

  const getError = (id?: string, name?: string) => {
    if (errors?.id === id && Object.keys(errors as ErrorsMap)[0] === name) {
      return t(errors?.name || "")
    }
  }

  const disabled = !hasAuthority(["AUTH_EDIT_MEDIA_TYPE"])

  const onToggle = (
    evt: ChangeEvent<HTMLInputElement> | undefined,
    mediaFieldId?: string | undefined,
  ) => {
    const value = evt?.target.checked
    const name = evt?.target.name
    const { name: path = "" } = evt?.target ?? {}
    !!onUpdate && onUpdate(name, path, value, mediaFieldId)
  }

  return (
    <div>
      {!data.length ? (
        <div className={styles.empty}>
          <span>{t("submediaTypes.empty")}</span>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <tbody>
              <tr>
                <td className="BodyBody-sBlackBold">
                  {t("admin.mediaTypes.semantic")}
                </td>
                <td className={cx("BodyBody-sBlackBold", styles.indentedLabel)}>
                  {t("admin.mediaTypes.label")}
                </td>
                {hasKey("type") ? (
                  <td
                    className={cx("BodyBody-sBlackBold", styles.indentedLabel)}
                  >
                    {t("admin.mediaTypes.type")}
                  </td>
                ) : null}
                {hasKey("category") ? (
                  <td
                    className={cx("BodyBody-sBlackBold", styles.indentedLabel)}
                  >
                    {t("admin.mediaTypes.category")}
                  </td>
                ) : null}
                {hasKey("required") ? (
                  <td className="BodyBody-sBlackBold">
                    {t("admin.mediaTypes.required")}
                  </td>
                ) : null}
                <WithAuthority allowed={["AUTH_EDIT_MEDIA_TYPE"]}>
                  <>
                    {hasKey("required") ? (
                      <td
                        className={cx(
                          "BodyBody-sBlackBold",
                          styles.actionlabel,
                        )}
                      >
                        {t("admin.mediaTypes.actions")}
                      </td>
                    ) : (
                      <td className="BodyBody-sBlackBold">
                        {t("admin.mediaTypes.actions")}
                      </td>
                    )}
                  </>
                </WithAuthority>
              </tr>
              {data.map((item, idx) => {
                return (
                  <tr key={idx}>
                    <td className={styles.cell}>{item.semanticName}</td>
                    <td className={styles.cell}>
                      {!disabled ? (
                        <GhostTextInput
                          name={`${idx}.name`}
                          onChange={onChange}
                          value={item?.name}
                          onBlur={(e) => handleBlur(e, "name", item?.id)}
                          className={styles.input}
                          error={!!getError(item?.id, "name")}
                        />
                      ) : (
                        <div className={styles.readonly}>{item?.name}</div>
                      )}
                      {!!getError(item?.id, "name") ? (
                        <div className={styles.helper}>
                          {getError(item?.id, "name")}
                        </div>
                      ) : null}
                    </td>
                    {!!item?.type && types?.length ? (
                      <td className={styles.cell}>
                        {!disabled ? (
                          <Select
                            name={`${idx}.type`}
                            value={item?.type}
                            onChange={onChange}
                            onBlur={(e) => handleBlur(e, "type", item?.id)}
                            options={types || []}
                            placeholder={""}
                            className={styles.select}
                            error={!!getError(item?.id, "type")}
                          />
                        ) : (
                          <div className={styles.readonly}>{item?.type}</div>
                        )}
                        {!!getError(item?.id, "type") ? (
                          <div className={styles.helper}>
                            {getError(item?.id, "type")}
                          </div>
                        ) : null}
                      </td>
                    ) : null}
                    {hasKey("required") ? (
                      <span className={cx(styles.space)}></span>
                    ) : null}

                    {item?.required !== undefined ? (
                      <>
                        <Switch
                          name={"required"}
                          defaultChecked={item?.required}
                          onChange={(e) => onToggle(e, item?.id)}
                          disabled={
                            (!!item?.id &&
                              !hasAuthority(["AUTH_EDIT_MEDIA_TYPE"])) ||
                            !!item?.requiredDisabled
                          }
                          color={
                            hasAuthority(["AUTH_EDIT_MEDIA_TYPE"])
                              ? "primary"
                              : undefined
                          }
                        />
                        <span className={cx(styles.switchcell)}></span>
                      </>
                    ) : hasKey("required") ? (
                      <span className={cx(styles.undefinedcell)}></span>
                    ) : null}
                    <WithAuthority allowed={["AUTH_EDIT_MEDIA_TYPE"]}>
                      <td className={cx(styles.cell, styles.lastcell)}>
                        {hasKey("required") ? (
                          <span className={cx(styles.action)}></span>
                        ) : null}

                        <MoreActions
                          options={[
                            {
                              label: t("agency.options.delete"),
                              icon: <DeleteIcon />,
                              onClick: () =>
                                !!handleDelete && handleDelete(item?.id),
                            },
                          ]}
                        />
                      </td>
                    </WithAuthority>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const styles = {
  empty: css`
    display: grid;
    place-items: center;
    color: var(--dark-grey);
    height: 80px;
    font-weight: 300;
  `,
  tableContainer: css`
    margin-top: var(--spacing-xxl-2);
    min-height: 80px;
  `,
  table: css`
    width: 100%;
    border-spacing: 0;
    border-collapse: separate;
  `,
  cell: css`
    padding: 4px 2px;
    text-align: left;
    vertical-align: baseline;
    font-size: 0.8125rem;
    min-width: 90px;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    border-bottom: 1.775px solid var(--light-grey);
  `,
  switchcell: css`
   padding: 4px 2px;
   display: inline-block;
   position: relative;
   vertical-align: baseline;
   after: position: absolute;
   content: '';
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
   border-bottom: 2px solid var(--light-grey);
   width: 100%;
   transform: translateX(-50%);
   bottom: -4px;
   left: 50%;
  `,

  lastcell: css`
    width: 80px;
  `,
  action: css`
    margin-left: 25px;
  `,
  actionlabel: css`
    text-align: center;
  `,
  space: css`
    margin-left: 20px;
  `,
  required: css`
    margin-left: 15px;
  `,
  indentedLabel: css`
    padding-left: 14px;
  `,
  readonly: css`
    height: var(--spacing-xxl-2);
    padding: var(--spacing-m) var(--spacing-s);
    margin-bottom: var(--spacing-s);
  `,
  input: css`
    height: var(--spacing-xxl-2);
    font-size: 0.8125rem;
    :hover {
      border-style: dashed;
    }
  `,
  select: css`
    .MuiSelect-outlined.MuiSelect-outlined {
      padding: 6px 12px;
    }
    &.MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline {
      border: none;
    }
    &.MuiOutlinedInput-root.Mui-disabled
      .MuiOutlinedInput-notchedOutline:hover {
      border: none;
    }
  `,
  helper: css`
    font-size: 0.625rem;
    color: var(--red);
  `,
  undefinedcell: css`
   padding: 4px 2px;
   display: inline-block;
   position: relative;
   vertical-align: baseline;
   after: position: absolute;
   content: '';
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
   border-bottom: 2px solid var(--light-grey);
   width: 100%;
   transform: translateX(-50%);
   bottom: -23.406px;
   left: 30%;
  `,
}
